// Update imports to include all needed icons
import { Shield, Cog, Eye, AlertTriangle, ArrowRight, Calculator, TrendingUp, DollarSign, Percent, Info, X, Plus, ChevronDown, ChevronUp, BarChart2 } from 'lucide-react';
import React, { useState } from 'react';
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ExitCondition, StrategyFormData, RiskParameters } from './types';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

// Define all needed interfaces
interface RiskManagementTabProps {
  formData: StrategyFormData;
  onRiskParamsChange: (changes: Partial<RiskParameters>) => void;
  onFormDataChange: (changes: Partial<StrategyFormData>) => void;
  exitConditions: ExitCondition[];
  onAddExitCondition: (type: string, threshold: string, operator: string) => void;
  onRemoveExitCondition: (id: string) => void;
  onUpdateExitCondition: (id: string, changes: Partial<ExitCondition>) => void;
  models: any[];
}

export const RiskManagementTab: React.FC<RiskManagementTabProps> = ({
  formData,
  onRiskParamsChange,
  onFormDataChange,
  exitConditions,
  onAddExitCondition,
  onRemoveExitCondition,
  onUpdateExitCondition,
  models
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [exitType, setExitType] = useState('');
  const [exitOperator, setExitOperator] = useState('');
  const [exitThreshold, setExitThreshold] = useState('');

  // Fix for Select components
  const handleRiskParamChange = (param: keyof RiskParameters, value: string | number | boolean) => {
    onRiskParamsChange({ [param]: value });
  };

  const handleAddExitCondition = () => {
    if (exitType && exitOperator && exitThreshold) {
      onAddExitCondition(
        exitType,
        exitThreshold,
        exitOperator
      );
      
      setExitType('');
      setExitOperator('');
      setExitThreshold('');
    }
  };

  const getIconForType = (type: string) => {
    switch (type) {
      case 'vix':
        return <TrendingUp className="h-4 w-4 mr-2" />;
      case 'rsi':
        return <BarChart2 className="h-4 w-4 mr-2" />;
      case 'sentiment':
        return <TrendingUp className="h-4 w-4 mr-2" />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Basic Risk Parameters Section */}
      <div>
        <h3 className="text-base font-medium mb-4 flex items-center gap-2">
          <Shield className="text-blue-600" size={18} />Overall Strategy Risk Parameters
        </h3>
        <div className="p-3 rounded-lg bg-blue-50 border border-blue-100 mb-4">
          <div className="flex items-start">
            <Info className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-700">
              These parameters set global limits for the entire strategy that take precedence over basket-level rules and AI decisions.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="max-position-size" className="block text-sm font-medium mb-1 flex items-center">
              Max Position Size (%)
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger type="button"><Info className="inline h-3 w-3 ml-1 text-gray-400 cursor-help" /></TooltipTrigger>
                  <TooltipContent className="max-w-[250px] text-xs">
                    Maximum percentage of strategy capital that can be allocated to any single stock position at initiation. 
                    Prevents concentration in individual assets regardless of signals or basket settings.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </Label>
            <div className="relative">
              <Input
                type="number"
                id="max-position-size"
                className="pr-7"
                placeholder="e.g., 10"
                value={formData.riskParameters?.maxPositionSizePct ? 
                  (formData.riskParameters.maxPositionSizePct * 100).toString() : 
                  ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? '' : parseFloat(e.target.value);
                  onRiskParamsChange({ 
                    maxPositionSizePct: value === '' ? undefined : value / 100 
                  });
                }}
                min="0"
                max="100"
                step="0.1"
              />
              <span className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-500">%</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Example: 10% means no more than 10% of capital in any one stock
            </p>
          </div>
          <div>
            <Label htmlFor="max-strategy-exposure" className="block text-sm font-medium mb-1 flex items-center">
              Max Strategy Exposure (%)
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger type="button"><Info className="inline h-3 w-3 ml-1 text-gray-400 cursor-help" /></TooltipTrigger>
                  <TooltipContent className="max-w-[250px] text-xs">
                    Maximum percentage of strategy capital that can be invested in the market at any time.
                    Controls overall market exposure and cash buffer regardless of individual position signals.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </Label>
            <div className="relative">
              <Input
                type="number"
                id="max-strategy-exposure"
                className="pr-7"
                placeholder="e.g., 80"
                value={formData.riskParameters?.maxStrategyExposurePct ? 
                  (typeof formData.riskParameters.maxStrategyExposurePct === 'number' ? 
                    formData.riskParameters.maxStrategyExposurePct : 
                    formData.riskParameters.maxStrategyExposurePct).toString() : 
                  '100'}
                onChange={(e) => {
                  const value = e.target.value === '' ? '' : parseFloat(e.target.value);
                  onRiskParamsChange({ 
                    maxStrategyExposurePct: value === '' ? 100 : parseFloat(value) 
                  });
                }}
                min="0" 
                max="100"
                step="0.1"
              />
              <span className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-500">%</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Example: 80% means 20% of capital remains in cash
            </p>
          </div>
        </div>
      </div>

      {/* Advanced Risk Settings Section */}
      <div>
        <h3 className="text-base font-medium mb-4 flex items-center gap-2">
          <Cog className="text-blue-600" size={18} />Advanced Risk Settings
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50/50">
            <Switch
              id="vol-weighted-risk"
              checked={formData.riskParameters?.isVolWeightedRisk || false}
              onCheckedChange={(checked) => onRiskParamsChange({ isVolWeightedRisk: checked })}
            />
            <Label htmlFor="vol-weighted-risk" className="text-sm font-medium text-gray-700 flex-1 pr-2 cursor-pointer">
              Volatility Weighted Risk 
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger type="button"><Info className="inline h-3 w-3 ml-1 text-gray-400 cursor-help" /></TooltipTrigger>
                  <TooltipContent className="max-w-[250px] text-xs">
                    Automatically adjust position sizes based on asset volatility. 
                    Higher volatility assets receive smaller positions, while lower 
                    volatility assets receive larger positions (within Max Position Size limits).
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </Label>
          </div>
          <div className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50/50">
            <Switch
              id="dynamic-exits"
              checked={formData.riskParameters?.allowDynamicExits || false}
              onCheckedChange={(checked) => onRiskParamsChange({ allowDynamicExits: checked })}
              disabled={models.some(m => m.type === 'stewie_quant')}
            />
            <Label htmlFor="dynamic-exits" className={`text-sm font-medium flex-1 pr-2 cursor-pointer ${models.some(m => m.type === 'stewie_quant') ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700'}`}>
              Dynamic Exits 
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger type="button"><Info className="inline h-3 w-3 ml-1 text-gray-400 cursor-help" /></TooltipTrigger>
                  <TooltipContent className="max-w-[250px] text-xs">
                    Allow Stewie's AI to dynamically adjust exit conditions based on market conditions,
                    stock-specific volatility, and momentum indicators. 
                    <span className="block mt-1 text-blue-500 font-medium">Note: Always operates within the global risk parameters.</span>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </Label>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-base font-medium mb-4 flex items-center gap-2">
          <TrendingUp className="text-blue-600" size={18} />
          Custom Exit Conditions
        </h3>
        <div className="p-3 rounded-lg bg-blue-50 border border-blue-100 mb-4">
          <div className="flex items-start">
            <Info className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-700">
              Define market-based exit triggers that work in conjunction with strategy-wide risk parameters.
              These are evaluated on each trading cycle and can cause exits even if position targets aren't met.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="exit-type" className="block text-sm font-medium mb-1 flex items-center">
              Exit Trigger
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger type="button"><Info className="inline h-3 w-3 ml-1 text-gray-400 cursor-help" /></TooltipTrigger>
                  <TooltipContent className="max-w-[250px] text-xs">
                    Market condition or indicator that will trigger position exits.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </Label>
            <Select
              value={exitType}
              onValueChange={setExitType}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select trigger" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="vix">
                  <div className="flex items-center">
                    {getIconForType('vix')}
                    VIX Level
                  </div>
                </SelectItem>
                <SelectItem value="rsi">
                  <div className="flex items-center">
                    {getIconForType('rsi')}
                    Sector RSI
                  </div>
                </SelectItem>
                <SelectItem value="sentiment">
                  <div className="flex items-center">
                    {getIconForType('sentiment')}
                    Net Sentiment
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="exit-operator" className="block text-sm font-medium mb-1 flex items-center">
              Operator
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger type="button"><Info className="inline h-3 w-3 ml-1 text-gray-400 cursor-help" /></TooltipTrigger>
                  <TooltipContent className="max-w-[250px] text-xs">
                    The condition that defines when the exit will be triggered.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </Label>
            <Select
              value={exitOperator}
              onValueChange={setExitOperator}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select operator" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="above">Goes Above</SelectItem>
                <SelectItem value="below">Goes Below</SelectItem>
                <SelectItem value="crosses">Crosses</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="exit-threshold" className="block text-sm font-medium mb-1 flex items-center">
              Threshold
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger type="button"><Info className="inline h-3 w-3 ml-1 text-gray-400 cursor-help" /></TooltipTrigger>
                  <TooltipContent className="max-w-[250px] text-xs">
                    The value that will trigger the exit when compared with the indicator using the specified operator.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </Label>
            <Input
              type="number"
              id="exit-threshold"
              className="w-full"
              placeholder="Value"
              value={exitThreshold}
              onChange={(e) => setExitThreshold(e.target.value)}
            />
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={handleAddExitCondition}
          className="w-full rounded-xl mt-3"
          disabled={!exitType || !exitOperator || !exitThreshold}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Exit Rule
        </Button>

        <div className="mt-4 space-y-3">
          {exitConditions.length > 0 ? (
            exitConditions.map((condition, index) => (
              <div key={index} className="flex items-center gap-2 p-3 border rounded-lg bg-gray-50">
                <div className="flex-1">
                  <div className="text-sm font-medium">{condition.type}</div>
                  <div className="text-xs text-gray-500">
                    {condition.operator || ''} {condition.value || condition.threshold || ''}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => onRemoveExitCondition(condition.id!)}
                  className="rounded-xl text-red-500 hover:bg-red-50"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))
          ) : (
            <div className="p-3 rounded-lg border border-dashed border-gray-300 bg-gray-50 text-center">
              <p className="text-sm text-gray-500">No custom exit conditions defined. Strategy will use default global risk parameters.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
